import { useState } from "react";
import { useSnackbar } from "notistack";
import { Category } from "../../../../domain/model/Category";
import { GetCategoryUseCase } from "../../../../domain/usecase/category/get/GetUseCase";
import { State } from "../../../../domain/model/ResponseState";
import { CreateCategoryUseCase } from "../../../../domain/usecase/category/create/CreateUseCase";
import { DeleteCategoryUseCase } from "../../../../domain/usecase/category/delete/DeleteUseCase";
import { UpdateCategoryUseCase } from "../../../../domain/usecase/category/update/UpdateUseCase";
import { useNavigate } from "react-router-dom";
import { ROUTE_ENDPOINTS } from "../../../../data/constant/RouteConstants";
import { UploadFileUseCase } from "../../../../domain/usecase/uploadFile/UploadFileUseCase";
import { Attachment } from "../../../../domain/model/Attachment";

import Compressor from "compressorjs";
import {
  downloadBlob,
  showGlobalLoadingView,
} from "../../../../core/utils/Utils";
import { CreateStoreUseCase } from "../../../../domain/usecase/store/create/CreateUseCase";
import { GetStoreUseCase } from "../../../../domain/usecase/store/get/GetUseCase";
import { DeleteStoreUseCase } from "../../../../domain/usecase/store/delete/DeleteUseCase";
import { UpdateStoreUseCase } from "../../../../domain/usecase/store/update/UpdateUseCase";
import { Store } from "../../../../domain/model/Store";
import { GetPerProductReportUseCase } from "../../../../domain/usecase/report/getPerProduct/UseCase";

export function StoreDetailsViewModel(
  getStoreUseCase: GetStoreUseCase,
  createStoreUseCase: CreateStoreUseCase,
  deleteStoreUseCase: DeleteStoreUseCase,
  updateStoreUseCase: UpdateStoreUseCase,
  uploadFileUseCase: UploadFileUseCase,
  getPerProductReportUseCase: GetPerProductReportUseCase
) {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [isLoading, setLoading] = useState(false);
  const [store, setStore] = useState<Store | undefined>({});

  async function fetchStore(id: string) {
    setLoading(true);
    const response = await getStoreUseCase.invoke(id);
    switch (response.responseState) {
      case State.Success:
        setStore(response.data);
        break;
      case State.Fail:
      case State.Error:
        showSnackbar(response.error?.message);
        break;
    }
    setLoading(false);
  }

  async function deleteStore() {
    if (!store?._id) return;

    showGlobalLoadingView(true);
    const response = await deleteStoreUseCase.invoke(store?._id);
    showGlobalLoadingView(false);
    switch (response.responseState) {
      case State.Success:
        showSnackbar(response.data?.message, "success");
        navigate(ROUTE_ENDPOINTS.HOME_STORES);
        break;
      case State.Fail:
      case State.Error:
        showSnackbar(response.error?.message);
        break;
    }
  }

  async function saveStore() {
    if (!store?._id) return;

    if (!store.name) {
      showSnackbar("You should enter a name");
      return;
    }
    if (!store.branchName) {
      showSnackbar("You should enter the branch name");
      return;
    }
    if (!store.branchAddress) {
      showSnackbar("You should enter the branch address");
      return;
    }
    if (!store.branchNumber) {
      showSnackbar("You should enter the branch number");
      return;
    }
    if (!store.phoneNumber) {
      showSnackbar("You should enter a phone number");
      return;
    }
    if (!store.contactPerson) {
      showSnackbar("You should enter the contact person");
      return;
    }

    showGlobalLoadingView(true);

    if (store.logoFile) {
      const attachment = await uploadImage(store.logoFile);
      if (!attachment) {
        showGlobalLoadingView(false);
        return;
      }
      store.logo = attachment.downloadUrl;
    }

    const response = await updateStoreUseCase.invoke(store);
    showGlobalLoadingView(false);
    switch (response.responseState) {
      case State.Success:
        showSnackbar(response.data?.message, "success");
        navigate(ROUTE_ENDPOINTS.HOME_STORES);
        break;
      case State.Fail:
      case State.Error:
        showSnackbar(response.error?.message);
        break;
    }
  }

  async function createStore() {
    if (!store) return;
    if (!store.logoFile) {
      showSnackbar("You should choose an image");
      return;
    }
    if (!store.name) {
      showSnackbar("You should enter a name");
      return;
    }
    if (!store.branchName) {
      showSnackbar("You should enter the branch name");
      return;
    }
    if (!store.branchAddress) {
      showSnackbar("You should enter the branch address");
      return;
    }
    if (!store.branchNumber) {
      showSnackbar("You should enter the branch number");
      return;
    }
    if (!store.phoneNumber) {
      showSnackbar("You should enter a phone number");
      return;
    }
    if (!store.contactPerson) {
      showSnackbar("You should enter the contact person");
      return;
    }
    showGlobalLoadingView(true);

    const attachment = await uploadImage(store?.logoFile);
    if (!attachment) {
      showGlobalLoadingView(false);
      return;
    }
    store.logo = attachment.downloadUrl;

    const response = await createStoreUseCase.invoke(store);
    showGlobalLoadingView(false);
    switch (response.responseState) {
      case State.Success:
        showSnackbar(response.data?.message, "success");
        navigate(ROUTE_ENDPOINTS.HOME_STORES);
        break;
      case State.Fail:
      case State.Error:
        showSnackbar(response.error?.message);
        break;
    }
  }

  async function uploadImage(file: File): Promise<Attachment | undefined> {
    const compressedFile: File | Blob = await new Promise((resolve, reject) => {
      new Compressor(file, {
        quality: 0.8,
        maxHeight: 1800,
        maxWidth: 1800,
        success(result) {
          resolve(result);
        },
        error(error) {
          reject(error);
        },
      });
    });
    const formData = new FormData();
    formData.append("file", compressedFile);
    const response = await uploadFileUseCase.invoke(formData);
    switch (response.responseState) {
      case State.Success:
        return response.data;
      case State.Fail:
      case State.Error:
        showSnackbar(response.error?.message);
        break;
    }
  }

  function setLogo(file: File) {
    if (!file) return;
    setStore({
      ...store,
      logo: URL.createObjectURL(file),
      logoFile: file,
    });
  }

  function setName(name: string) {
    setStore({
      ...store,
      name: name,
    });
  }

  function setBranchName(txt: string) {
    setStore({
      ...store,
      branchName: txt,
    });
  }

  function setBranchAddress(txt: string) {
    setStore({
      ...store,
      branchAddress: txt,
    });
  }

  function setBranchNumber(txt: string) {
    setStore({
      ...store,
      branchNumber: txt,
    });
  }

  function setPhoneNumber(txt: string) {
    setStore({
      ...store,
      phoneNumber: txt,
    });
  }

  function setContactPerson(txt: string) {
    setStore({
      ...store,
      contactPerson: txt,
    });
  }

  async function getPerProductReport(startDate?: Date, endDate?: Date) {
    if (!store) return;
    
    const startDateString = startDate ? startDate.toUTCString() : undefined;
    const endDateString = endDate ? endDate.toUTCString() : undefined;

    showGlobalLoadingView(true);
    const response = await getPerProductReportUseCase.invoke({
      dateRange: {
        fromUTC: startDateString,
        toUTC: endDateString,
      },
      storeId: store?._id,
      storeName: store?.name,
    });
    showGlobalLoadingView(false);
    switch (response.responseState) {
      case State.Success:
        if (!response.data) return;
        downloadBlob(response.data);
        break;
      case State.Fail:
      case State.Error:
        showSnackbar(response.error?.message);
        break;
    }
  }

  function setCustomDate(
    startDate: Date | null | undefined,
    endDate: Date | null | undefined
  ) {
    if (!startDate) return;
    if (!endDate) return;

    if (endDate <= startDate) {
      showSnackbar("End date cannot be less than end date");
      return;
    }

    getPerProductReport(startDate, endDate);
  }

  function showSnackbar(
    message: string | undefined,
    variant: "error" | "success" = "error"
  ) {
    if (message) enqueueSnackbar(message, { variant: variant });
  }

  return {
    isLoading,
    store,
    fetchStore,
    setLogo,
    setName,
    deleteStore,
    saveStore,
    createStore,
    setBranchName,
    setBranchAddress,
    setBranchNumber,
    setContactPerson,
    setPhoneNumber,
    setCustomDate,
  };
}
